import {
  Button,
  ButtonVariants,
  Colors,
  TextBold,
  TextReg,
  useCheckoutModalStore,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import React, { useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useOrderCalculations } from '../hooks/useOrderCalculations';
import { useCheckoutFormStore } from '../stores/useCheckoutFormStore';
import { StripeElements } from '../stripe/StripeElements';
import { useStripe } from '../stripe/useStripe';
import { CardForm } from './CardForm';
import { OrderSummary } from './OrderSummary';

// Payment content component that uses Stripe hooks
function PaymentContent() {
  const styles = useDynamicStyles(createStyles);
  const { ticketQuantities, appliedPromo, setClientSecret } =
    useCheckoutFormStore();
  const { setCurrentStep, setCheckoutId } = useCheckoutModalStore();
  const [cardComplete, setCardComplete] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const { confirmPayment } = useStripe();
  const [error, setError] = useState<string>();

  // Get calculated total for button text (shares cache with OrderSummary)
  const {
    data: calculatedTotal,
    isError,
    isCalculating,
  } = useOrderCalculations({
    ticketQuantities,
    appliedPromo,
  });

  const handleSubmit = async () => {
    if (!cardComplete) return;

    setError(undefined);
    setIsConfirming(true);

    // Confirm payment with Stripe
    const { error } = await confirmPayment();

    if (error) {
      setError(error.message);
      setIsConfirming(false);
      return;
    }

    setIsConfirming(false);
    // Navigate to summary step to poll for completion
    setCurrentStep('summary');
  };

  return (
    <View style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        keyboardDismissMode="interactive"
        showsVerticalScrollIndicator={false}
      >
        <OrderSummary
          ticketQuantities={ticketQuantities}
          appliedPromo={appliedPromo}
          isExpandable={true}
        />

        <CardForm
          onComplete={(complete) => {
            setCardComplete(complete);
            if (error) {
              setError(undefined);
            }
          }}
        />
        {error && <TextReg style={styles.errorText}>{error}</TextReg>}
      </ScrollView>

      <View style={styles.footer}>
        <Button
          variant={ButtonVariants.WHITE}
          size="medium"
          onPress={() => {
            setClientSecret(undefined);
            setCheckoutId(undefined);
            setCurrentStep('attendeeDetails');
          }}
          style={styles.button}
        >
          Back
        </Button>
        <Button
          variant={ButtonVariants.PURPLE}
          size="medium"
          onPress={handleSubmit}
          style={styles.button}
          disabled={!cardComplete || isConfirming || isError || isCalculating}
          loading={isConfirming || isCalculating}
          hideChildrenOnLoading
        >
          {(calculatedTotal?.total ?? 0) > 0
            ? `Pay $${calculatedTotal?.total?.toFixed(2)}`
            : 'Register'}
        </Button>
      </View>
    </View>
  );
}

// Main component that wraps with StripeProvider
export function PaymentStep() {
  const { clientSecret } = useCheckoutFormStore();
  const { setCurrentStep, setCheckoutId } = useCheckoutModalStore();
  const styles = useDynamicStyles(createStyles);

  // Note: this should never happen!
  if (!clientSecret) {
    return (
      <View style={styles.errorContainer}>
        <View style={styles.errorContent}>
          <TextBold style={styles.errorTitle}>Error</TextBold>
          <TextReg style={styles.errorMessage}>
            Please check your details and try again
          </TextReg>
        </View>
        <View style={styles.footer}>
          <Button
            variant={ButtonVariants.WHITE}
            onPress={() => {
              setCheckoutId(undefined);
              setCurrentStep('attendeeDetails');
            }}
          >
            Back
          </Button>
        </View>
      </View>
    );
  }

  return (
    <StripeElements clientSecret={clientSecret}>
      <PaymentContent />
    </StripeElements>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    container: {
      flex: 1,
      width: '100%',
    },
    scrollView: {
      flex: 1,
    },
    scrollContent: {
      padding: isMobile ? 12 : 20,
      alignItems: 'center',
      gap: 24,
    },
    paymentCard: {
      width: '100%',
      maxWidth: isMobile ? 500 : 700,
      padding: isMobile ? 16 : 24,
      gap: 16,
    },
    sectionTitle: {
      fontSize: 18,
    },
    cardFieldContainer: {
      width: '100%',
    },
    errorText: {
      color: Colors.error,
      fontSize: 14,
      width: '100%',
    },
    footer: {
      padding: isMobile ? 12 : 16,
      gap: 16,
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'row',
    },
    button: {
      minWidth: isMobile ? 120 : 150,
    },
    errorContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
    errorContent: {
      gap: 16,
      flex: 1,
      justifyContent: 'center',
    },
    errorTitle: {
      fontSize: 24,
      color: Colors.error,
      textAlign: 'center',
    },
    errorMessage: {
      fontSize: 16,
      textAlign: 'center',
    },
  });
